/**
 * BUSCA GLOBAL (CERES-UX-001): empresa, pessoa ou negócio, de qualquer tela.
 *
 * Uma consulta só (`GET /insights/busca`), que o servidor resolve com o ESCOPO e
 * as permissões de quem pergunta — o resultado nunca mostra o que a pessoa não
 * poderia abrir. Cada grupo só vem quando a pessoa tem a permissão e o tenant,
 * a capacidade; o que não veio por isso é dito, e não escondido.
 *
 * Teclado: setas percorrem os resultados, Enter abre, Escape fecha e devolve o
 * foco ao campo. O padrão é o combobox da ARIA (campo + lista de opções).
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, toQuery } from '../../lib/api';
import { t, type ChaveDeTexto } from '../../lib/i18n';
import { IconSearch, Spinner } from '../ui';

interface ResultadoDaBusca {
  tipo: 'EMPRESA' | 'PESSOA' | 'NEGOCIO';
  id: string;
  titulo: string;
  detalhe: string | null;
}

interface RespostaDaBusca {
  resultados: ResultadoDaBusca[];
}

const ROTA: Record<ResultadoDaBusca['tipo'], (id: string) => string> = {
  EMPRESA: (id) => `/empresas/${id}`,
  PESSOA: (id) => `/contatos/${id}`,
  NEGOCIO: (id) => `/oportunidades/${id}`,
};

const ROTULO: Record<ResultadoDaBusca['tipo'], ChaveDeTexto> = {
  EMPRESA: 'ux.busca.tipoEmpresa',
  PESSOA: 'ux.busca.tipoPessoa',
  NEGOCIO: 'ux.busca.tipoNegocio',
};

/** Espera a pessoa parar de digitar antes de consultar. */
function useAtrasado(valor: string, ms = 250): string {
  const [atrasado, setAtrasado] = useState(valor);
  useEffect(() => {
    const id = window.setTimeout(() => setAtrasado(valor), ms);
    return () => window.clearTimeout(id);
  }, [valor, ms]);
  return atrasado;
}

function CampoDeBusca({ aoAbrir, autoFocus = false }: { aoAbrir?: () => void; autoFocus?: boolean }): JSX.Element {
  const navigate = useNavigate();
  const idDaLista = useId();
  const campoRef = useRef<HTMLInputElement>(null);
  const [termo, setTermo] = useState('');
  const [aberto, setAberto] = useState(false);
  const [indice, setIndice] = useState(-1);
  const termoAtrasado = useAtrasado(termo.trim());
  const consultar = termoAtrasado.length >= 2;

  const busca = useQuery({
    queryKey: ['busca-global', termoAtrasado],
    enabled: consultar,
    queryFn: ({ signal }) =>
      api.get<RespostaDaBusca>(`/insights/busca${toQuery({ q: termoAtrasado })}`, signal),
    staleTime: 30_000,
  });

  const resultados = useMemo(() => (consultar ? (busca.data?.resultados ?? []) : []), [busca.data, consultar]);

  useEffect(() => setIndice(-1), [termoAtrasado]);

  const abrir = (resultado: ResultadoDaBusca): void => {
    setAberto(false);
    setTermo('');
    aoAbrir?.();
    navigate(ROTA[resultado.tipo](resultado.id));
  };

  const aoTeclar = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAberto(true);
      setIndice((i) => Math.min(resultados.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndice((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      const escolhido = resultados[indice] ?? (resultados.length === 1 ? resultados[0] : undefined);
      if (escolhido !== undefined) {
        e.preventDefault();
        abrir(escolhido);
      }
    } else if (e.key === 'Escape') {
      setAberto(false);
      setIndice(-1);
    }
  };

  const mostrarLista = aberto && termo.trim().length >= 2;
  const idDaOpcao = (i: number): string => `${idDaLista}-${i}`;

  return (
    <div className="relative w-full">
      <label className="flex h-10 w-full items-center gap-2 rounded-lg border border-line bg-surface-sunken px-3 text-ink-subtle focus-within:border-brand-500 focus-within:bg-surface">
        <IconSearch className="h-4 w-4 shrink-0" />
        <span className="sr-only">{t('ux.busca.rotulo')}</span>
        <input
          ref={campoRef}
          type="search"
          role="combobox"
          aria-expanded={mostrarLista}
          aria-controls={idDaLista}
          aria-autocomplete="list"
          aria-activedescendant={indice >= 0 ? idDaOpcao(indice) : undefined}
          autoFocus={autoFocus}
          value={termo}
          placeholder={t('ux.busca.placeholder')}
          onChange={(e) => {
            setTermo(e.target.value);
            setAberto(true);
          }}
          onFocus={() => setAberto(true)}
          onBlur={() => window.setTimeout(() => setAberto(false), 150)}
          onKeyDown={aoTeclar}
          className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-0"
        />
        {busca.isFetching && consultar && <Spinner className="h-3.5 w-3.5" />}
      </label>

      {mostrarLista && (
        <div className="absolute right-0 top-full z-40 mt-1 w-full min-w-[18rem] overflow-hidden rounded-xl border border-line bg-surface py-1 shadow-pop">
          <ul id={idDaLista} role="listbox" aria-label={t('ux.busca.rotulo')}>
            {resultados.map((resultado, i) => (
              <li
                key={`${resultado.tipo}:${resultado.id}`}
                id={idDaOpcao(i)}
                role="option"
                aria-selected={i === indice}
                onMouseDown={(e) => {
                  e.preventDefault();
                  abrir(resultado);
                }}
                className={`flex min-h-[2.75rem] cursor-pointer items-center gap-3 px-3 py-1.5 ${i === indice ? 'bg-brand-50' : 'hover:bg-surface-sunken'}`}
              >
                <span className="badge-neutral shrink-0">{t(ROTULO[resultado.tipo])}</span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-ink">{resultado.titulo}</span>
                  {resultado.detalhe !== null && (
                    <span className="block truncate text-2xs text-ink-subtle">{resultado.detalhe}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
          {consultar && !busca.isFetching && busca.isError && (
            <p className="px-3 py-2 text-xs text-danger-700" role="alert">{t('ux.busca.erro')}</p>
          )}
          {consultar && !busca.isFetching && !busca.isError && resultados.length === 0 && (
            <p className="px-3 py-2 text-xs text-ink-muted">{t('ux.busca.nada')}</p>
          )}
          {!consultar && <p className="px-3 py-2 text-xs text-ink-muted">{t('ux.busca.digite')}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * `compacta`: no celular a busca é um botão; tocar abre o campo por cima do
 * cabeçalho, com foco já nele.
 */
export function BuscaGlobal({ compacta = false }: { compacta?: boolean }): JSX.Element {
  const [aberta, setAberta] = useState(false);

  if (!compacta) {
    return (
      <div className="w-72 xl:w-80">
        <CampoDeBusca />
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setAberta((v) => !v)}
        aria-expanded={aberta}
        aria-label={t('ux.busca.rotulo')}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-brand-100 hover:bg-brand-700"
      >
        <IconSearch className="h-5 w-5" />
      </button>
      {aberta && (
        <div className="absolute inset-x-0 top-full z-30 border-b border-line bg-surface p-3 shadow-pop">
          <CampoDeBusca autoFocus aoAbrir={() => setAberta(false)} />
        </div>
      )}
    </>
  );
}
